// AppNav — single source of truth for top-level page navigation.
//
// Renders a compact horizontal menu with Home / Flow-weaver / Atlas /
// Process Engine. Highlights the active route via NavLink's data-state.
//
// Tier gating:
//   - Home + Flow-weaver: always visible (Free+)
//   - Atlas: hidden when `!atlasEnabled` (Free / Advanced)
//   - Process Engine: hidden when `!processEngineEnabled` (Free / Advanced)
//
// Why hide rather than disable: locked routes redirect to /pricing
// anyway, so showing them in the nav would mostly produce dead-ends.
// Users on free plans see fewer items but each one works.
//
// Mobile (<768px): switches to icon-only mode so all 4 items fit even
// on the narrowest phones.

import { NavLink } from 'react-router-dom';
import { Home, Workflow, LayoutGrid, Cog, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAtlasCapabilities } from '@/hooks/useAtlasCapabilities';
import { useProcessEngineCapabilities } from '@/hooks/useProcessEngineCapabilities';
import { useFinalCapabilities } from '@/hooks/useFinalCapabilities';

interface AppNavProps {
  /** Optional extra className for outer wrapper. */
  className?: string;
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  testId: string;
}

export function AppNav({ className }: AppNavProps) {
  const isMobile = useIsMobile();
  const atlas = useAtlasCapabilities();
  const pe = useProcessEngineCapabilities();
  const finalCaps = useFinalCapabilities();

  const items: NavItem[] = [
    { to: '/', label: 'Home', icon: Home, testId: 'appnav-home' },
    { to: '/flow-weaver', label: 'Flow-weaver', icon: Workflow, testId: 'appnav-flow' },
  ];
  if (pe.processEngineEnabled) {
    items.push({
      to: '/processengine',
      label: 'Process Engine',
      icon: Cog,
      testId: 'appnav-process',
    });
  }
  if (atlas.atlasEnabled) {
    items.push({ to: '/atlas', label: 'Atlas', icon: LayoutGrid, testId: 'appnav-atlas' });
  }
  if (finalCaps.finalEnabled) {
    items.push({ to: '/final', label: 'Final', icon: FileText, testId: 'appnav-final' });
  }

  return (
    <nav
      className={cn(
        'flex items-center gap-0.5 rounded-md border bg-card/40 p-0.5',
        className,
      )}
      data-testid="app-nav"
    >
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/' || item.to === '/flow-weaver'}
          className={({ isActive }) =>
            cn(
              'inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors',
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )
          }
          data-testid={item.testId}
          title={item.label}
        >
          <item.icon className="h-3.5 w-3.5" />
          {!isMobile && <span>{item.label}</span>}
        </NavLink>
      ))}
    </nav>
  );
}
